import { useState } from "react";
import { Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LeadFormFields } from "./LeadFormFields";
import { GDPRConsent } from "./GDPRConsent";
import { CSVMappingDialog, requiredFields } from "./CSVMappingDialog";
import { NewsletterLead } from "./types";

interface LeadFormProps {
  onSuccess: () => void;
}

export const LeadForm = ({ onSuccess }: LeadFormProps) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvContent, setCsvContent] = useState<string>("");
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [newLead, setNewLead] = useState({
    email: "",
    full_name: "",
    company: "",
    role_title: "",
  });
  const [gdprConsent, setGdprConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [retentionPeriod, setRetentionPeriod] = useState("1 year");

  const handleAddLead = async () => {
    if (!newLead.email) {
      toast({
        title: "Error",
        description: "Email is required",
        variant: "destructive"
      });
      return;
    }

    if (!gdprConsent) {
      toast({
        title: "Error",
        description: "GDPR consent is required",
        variant: "destructive"
      });
      return;
    }

    try {
      const leadData = {
        ...newLead,
        category: 'manual' as const,
        source: 'manual',
        subscription_status: 'active',
        gdpr_consent: gdprConsent,
        gdpr_consent_date: new Date().toISOString(),
        data_retention_period: retentionPeriod,
        marketing_consent: marketingConsent
      };

      const { error } = await supabase
        .from('newsletter_leads')
        .insert(leadData);

      if (error) {
        console.error('Error adding lead:', error);
        toast({
          title: "Error adding lead",
          description: error.message,
          variant: "destructive"
        });
      } else {
        console.log('Lead added successfully');
        toast({
          title: "Success",
          description: "Lead added successfully"
        });
        setNewLead({
          email: "",
          full_name: "",
          company: "",
          role_title: "",
        });
        setGdprConsent(false);
        setMarketingConsent(false);
        onSuccess();
      }
    } catch (error) {
      console.error('Error in handleAddLead:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Error",
        description: "Please upload a CSV file",
        variant: "destructive"
      });
      return;
    }

    try {
      const content = await file.text();
      const lines = content.split('\n');
      const headers = lines[0].split(',').map(header => header.trim());
      
      setCsvHeaders(headers);
      setCsvContent(content);
      setShowMappingDialog(true);
      
      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      console.error('Error reading CSV:', error);
      toast({
        title: "Error",
        description: "Failed to read CSV file",
        variant: "destructive"
      });
    }
  };

  const handleFileUpload = async () => {
    if (!csvContent || Object.keys(columnMapping).length !== requiredFields.length) {
      toast({
        title: "Error",
        description: "Please map all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsUploading(true);
      const lines = csvContent.split('\n');
      const headers = lines[0].split(',').map(header => header.trim());
      
      const leadsToInsert: Array<Partial<NewsletterLead> & { category: string; email: string }> = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const values = line.split(',').map(value => value.trim());
        const lead: Partial<NewsletterLead> & { category: string; email: string } = {
          category: 'csv_import',
          email: '', // Will be populated from CSV
          source: 'csv_import',
          subscription_status: 'active',
          gdpr_consent: gdprConsent,
          gdpr_consent_date: new Date().toISOString(),
          data_retention_period: retentionPeriod as string,
          marketing_consent: marketingConsent
        };
        
        // Map values according to column mapping
        for (const [field, header] of Object.entries(columnMapping)) {
          const headerIndex = headers.indexOf(header);
          if (headerIndex !== -1 && values[headerIndex]) {
            (lead as any)[field] = values[headerIndex] as string;
          }
        }

        // Ensure required fields are present
        if (!lead.email) {
          console.error('Skipping row', i + 1, 'due to missing email');
          continue;
        }

        leadsToInsert.push(lead);
      }

      if (leadsToInsert.length > 0) {
        const { error } = await supabase
          .from('newsletter_leads')
          .insert(leadsToInsert);

        if (error) {
          console.error('Error importing leads:', error);
          toast({
            title: "Error",
            description: `Failed to import leads: ${error.message}`,
            variant: "destructive"
          });
        } else {
          toast({
            title: "Success",
            description: "CSV file imported successfully"
          });
          setShowMappingDialog(false);
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Error processing CSV:', error);
      toast({
        title: "Error",
        description: "Failed to process CSV file",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          <LeadFormFields newLead={newLead} setNewLead={setNewLead} />
          <Button onClick={handleAddLead} className="whitespace-nowrap">
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
          <div className="relative">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <Button variant="outline" disabled={isUploading} className="whitespace-nowrap">
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </div>
        </div>

        <GDPRConsent
          gdprConsent={gdprConsent}
          marketingConsent={marketingConsent}
          retentionPeriod={retentionPeriod}
          onGDPRConsentChange={setGdprConsent}
          onMarketingConsentChange={setMarketingConsent}
          onRetentionPeriodChange={setRetentionPeriod}
        />
      </div>

      <CSVMappingDialog
        showMappingDialog={showMappingDialog}
        setShowMappingDialog={setShowMappingDialog}
        csvHeaders={csvHeaders}
        columnMapping={columnMapping}
        setColumnMapping={setColumnMapping}
        isUploading={isUploading}
        handleFileUpload={handleFileUpload}
      />
    </>
  );
};